// src/prototype/contexts/EAContext.tsx
// ============================================================
// EA PROTOTYPE CONTEXT v4
// UPDATED: Added categoryWeights and measureWeights for 
// ranking customization (Step 1 → Step 2 data flow)
// UPDATED v4: demoProUnlocked now reads from localStorage on init
//   and syncs with useDemoPlan via dva_demo_plan_change event.
//   Fixes Execute step routing: EAActivatePage no longer redirects
//   to /upgrade when isPro is true in useDemoPlan.
// 
// This context is isolated to the prototype and does not affect
// production entitlements or billing.
// ============================================================

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { markPersonalizationChanged } from '../hooks/useEvaluationRun';
import {
  CategoryWeightsMap,
  MeasureWeightsMap,
  getDefaultCategoryWeightsMap,
  getDefaultMeasureWeightsMap,
  rebalanceCategoryWeights,
  rebalanceMeasureWeights,
  areCategoryWeightsDefault,
} from '../utils/weighting';

// Types for EA feedback collection
export type FlagType = 'incorrect' | 'missing' | 'too_aggressive' | 'too_conservative' | 'too_high' | 'too_low';

export interface EAFlag {
  id: string;
  targetType: 'assumption' | 'context' | 'opportunity';
  targetId: string;
  flagType: FlagType;
  comment?: string;
  timestamp: Date;
}

export interface EAEdit {
  id: string;
  targetType: 'context';
  targetId: string;
  originalValue: string;
  newValue: string;
  timestamp: Date;
}

interface EAContextType {
  // EA Mode detection
  isEAMode: boolean;

  // Feedback collection (stored in memory for prototype)
  flags: EAFlag[];
  addFlag: (flag: Omit<EAFlag, 'id' | 'timestamp'>) => void;
  removeFlag: (id: string) => void;
  
  edits: EAEdit[];
  addEdit: (edit: Omit<EAEdit, 'id' | 'timestamp'>) => void;
  
  // ============================================================
  // WEIGHTING CONFIGURATION (Step 1 → Step 2)
  // ============================================================
  
  // Category weights (6 categories, sum to 100%)
  categoryWeights: CategoryWeightsMap;
  updateCategoryWeight: (key: string, value: number) => void;
  resetCategoryWeights: () => void;
  areCategoryWeightsCustomized: boolean;
  
  // Measure weights (per-category, each sums to 100%)
  measureWeights: MeasureWeightsMap;
  updateMeasureWeight: (categoryKey: string, measureKey: string, value: number) => void;
  resetMeasureWeights: (categoryKey?: string) => void;
  resetAllMeasureWeights: () => void;
  areMeasureWeightsCustomized: boolean;
  
  // ============================================================
  // FINALIST MANAGEMENT (Step 2 → Step 3)
  // ============================================================
  
  // Selected finalists (managed in Step 2)
  finalistIds: Set<string>;
  setFinalistIds: (ids: Set<string>) => void;
  addFinalist: (id: string) => void;
  removeFinalist: (id: string) => void;
  toggleFinalist: (id: string) => void;
  
  // Priority ordering (can be reordered in Step 3)
  finalistOrder: string[];
  setFinalistOrder: (order: string[]) => void;
  moveFinalistUp: (id: string) => void;
  moveFinalistDown: (id: string) => void;
  
  // Flagged assumptions from Step 2 (read in Step 3)
  flaggedAssumptions: Map<string, FlagType>;
  setFlaggedAssumptions: (flags: Map<string, FlagType>) => void;
  setFlaggedAssumption: (oppId: string, assumptionId: string, flagType: FlagType | null) => void;
}

const EAContext = createContext<EAContextType | undefined>(undefined);

// LocalStorage keys for persistence
const STORAGE_KEYS = {
  FINALIST_IDS: 'ea_finalist_ids',
  FINALIST_ORDER: 'ea_finalist_order',
  FLAGGED_ASSUMPTIONS: 'ea_flagged_assumptions',
  CATEGORY_WEIGHTS: 'ea_category_weights',
  MEASURE_WEIGHTS: 'ea_measure_weights_v2', // P10: bumped so the 35/30/20/15 defaults replace any cached flat 25s
};

// Must match the key used in useDemoPlan.tsx
interface EAProviderProps {
  children: React.ReactNode;
}

export function EAProvider({ children }: EAProviderProps) {
  // Pro/plan state now lives in a single entitlement source (useEntitlements →
  // useDemoPlan); EAContext no longer mirrors demoProUnlocked.

  // Feedback collection - in-memory for prototype
  const [flags, setFlags] = useState<EAFlag[]>([]);
  const [edits, setEdits] = useState<EAEdit[]>([]);
  
  // ============================================================
  // WEIGHTING STATE (persisted to localStorage)
  // ============================================================
  
  const [categoryWeights, setCategoryWeightsState] = useState<CategoryWeightsMap>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.CATEGORY_WEIGHTS);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate the stored weights have expected keys
        if (typeof parsed === 'object' && 'costSavings' in parsed && 'strategicAlignment' in parsed) {
          return parsed as CategoryWeightsMap;
        }
      }
    } catch (e) {
      console.warn('[EA] Failed to load category weights from localStorage:', e);
    }
    return getDefaultCategoryWeightsMap();
  });
  
  const [measureWeights, setMeasureWeightsState] = useState<MeasureWeightsMap>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.MEASURE_WEIGHTS);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (typeof parsed === 'object' && 'costSavings' in parsed) {
          return parsed as MeasureWeightsMap;
        }
      }
    } catch (e) {
      console.warn('[EA] Failed to load measure weights from localStorage:', e);
    }
    return getDefaultMeasureWeightsMap();
  });
  
  // ============================================================
  // FINALIST STATE (persisted to localStorage)
  // ============================================================
  
  const [finalistIds, setFinalistIdsState] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.FINALIST_IDS);
      if (stored) {
        return new Set(JSON.parse(stored));
      }
    } catch (e) {
      console.warn('[EA] Failed to load finalist IDs from localStorage:', e);
    }
    return new Set();
  });
  
  const [finalistOrder, setFinalistOrderState] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.FINALIST_ORDER);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.warn('[EA] Failed to load finalist order from localStorage:', e);
    }
    return [];
  });
  
  const [flaggedAssumptions, setFlaggedAssumptionsState] = useState<Map<string, FlagType>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.FLAGGED_ASSUMPTIONS);
      if (stored) {
        return new Map(JSON.parse(stored));
      }
    } catch (e) {
      console.warn('[EA] Failed to load flagged assumptions from localStorage:', e);
    }
    return new Map();
  });
  
  // EAProvider only ever wraps EA routes (the promoted app + print pages),
  // so being mounted inside it means we are in EA mode.
  const isEAMode = typeof window !== 'undefined';
  
  // ============================================================
  // CATEGORY WEIGHT MANAGEMENT
  // ============================================================
  
  const updateCategoryWeight = useCallback((key: string, value: number) => {
    markPersonalizationChanged(); // weights are a personalization input — re-arm Evaluate
    setCategoryWeightsState(prev => {
      const newWeights = rebalanceCategoryWeights(prev, key, value);
      try {
        localStorage.setItem(STORAGE_KEYS.CATEGORY_WEIGHTS, JSON.stringify(newWeights));
      } catch (e) {
        console.warn('[EA] Failed to save category weights:', e);
      }
      console.log(`⚖️ Category weight updated: ${key} → ${value}%`, newWeights);
      return newWeights;
    });
  }, []);
  
  const resetCategoryWeights = useCallback(() => {
    const defaults = getDefaultCategoryWeightsMap();
    setCategoryWeightsState(defaults);
    try {
      localStorage.setItem(STORAGE_KEYS.CATEGORY_WEIGHTS, JSON.stringify(defaults));
    } catch (e) {
      console.warn('[EA] Failed to save category weights:', e);
    }
    console.log('⚖️ Category weights reset to defaults');
  }, []);
  
  const areCategoryWeightsCustomized = useMemo(() => {
    return !areCategoryWeightsDefault(categoryWeights);
  }, [categoryWeights]);
  
  // ============================================================
  // MEASURE WEIGHT MANAGEMENT
  // ============================================================
  
  const updateMeasureWeight = useCallback((categoryKey: string, measureKey: string, value: number) => {
    setMeasureWeightsState(prev => {
      const categoryMeasures = prev[categoryKey] || {};
      const newCategoryMeasures = rebalanceMeasureWeights(categoryMeasures, measureKey, value);
      const newWeights = { ...prev, [categoryKey]: newCategoryMeasures };
      
      try {
        localStorage.setItem(STORAGE_KEYS.MEASURE_WEIGHTS, JSON.stringify(newWeights));
      } catch (e) {
        console.warn('[EA] Failed to save measure weights:', e);
      }
      console.log(`📊 Measure weight updated: ${categoryKey}.${measureKey} → ${value}%`);
      return newWeights;
    });
  }, []);
  
  const resetMeasureWeights = useCallback((categoryKey?: string) => {
    setMeasureWeightsState(prev => {
      const defaults = getDefaultMeasureWeightsMap();
      let newWeights: MeasureWeightsMap;
      
      if (categoryKey) {
        // Reset only one category
        newWeights = { ...prev, [categoryKey]: defaults[categoryKey] };
        console.log(`📊 Measure weights reset for category: ${categoryKey}`);
      } else {
        // Reset all
        newWeights = defaults;
        console.log('📊 All measure weights reset to defaults');
      }
      
      try {
        localStorage.setItem(STORAGE_KEYS.MEASURE_WEIGHTS, JSON.stringify(newWeights));
      } catch (e) {
        console.warn('[EA] Failed to save measure weights:', e);
      }
      return newWeights;
    });
  }, []);
  
  const resetAllMeasureWeights = useCallback(() => {
    resetMeasureWeights();
  }, [resetMeasureWeights]);
  
  // Check if any measure weights are customized
  const areMeasureWeightsCustomized = useMemo(() => {
    const defaults = getDefaultMeasureWeightsMap();
    return JSON.stringify(measureWeights) !== JSON.stringify(defaults);
  }, [measureWeights]);
  
  // ============================================================
  // FINALIST ID MANAGEMENT
  // ============================================================
  
  const setFinalistIds = useCallback((ids: Set<string>) => {
    setFinalistIdsState(ids);
    try {
      localStorage.setItem(STORAGE_KEYS.FINALIST_IDS, JSON.stringify(Array.from(ids)));
    } catch (e) {
      console.warn('[EA] Failed to save finalist IDs:', e);
    }
    console.log('⭐ Finalists updated:', Array.from(ids));
  }, []);
  
  const addFinalist = useCallback((id: string) => {
    setFinalistIdsState(prev => {
      const next = new Set(prev);
      next.add(id);
      try {
        localStorage.setItem(STORAGE_KEYS.FINALIST_IDS, JSON.stringify(Array.from(next)));
      } catch (e) {
        console.warn('[EA] Failed to save finalist IDs:', e);
      }
      console.log('⭐ Finalist added:', id);
      return next;
    });
  }, []);
  
  const removeFinalist = useCallback((id: string) => {
    setFinalistIdsState(prev => {
      const next = new Set(prev);
      next.delete(id);
      try {
        localStorage.setItem(STORAGE_KEYS.FINALIST_IDS, JSON.stringify(Array.from(next)));
      } catch (e) {
        console.warn('[EA] Failed to save finalist IDs:', e);
      }
      console.log('⭐ Finalist removed:', id);
      return next;
    });
  }, []);
  
  const toggleFinalist = useCallback((id: string) => {
    setFinalistIdsState(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        console.log('⭐ Finalist removed:', id);
      } else {
        next.add(id);
        console.log('⭐ Finalist added:', id);
      }
      try {
        localStorage.setItem(STORAGE_KEYS.FINALIST_IDS, JSON.stringify(Array.from(next)));
      } catch (e) {
        console.warn('[EA] Failed to save finalist IDs:', e);
      }
      return next;
    });
  }, []);
  
  // ============================================================
  // FINALIST ORDER MANAGEMENT
  // ============================================================
  
  const setFinalistOrder = useCallback((order: string[]) => {
    setFinalistOrderState(order);
    try {
      localStorage.setItem(STORAGE_KEYS.FINALIST_ORDER, JSON.stringify(order));
    } catch (e) {
      console.warn('[EA] Failed to save finalist order:', e);
    }
    console.log('📋 Finalist order updated:', order);
  }, []);
  
  const moveFinalistUp = useCallback((id: string) => {
    setFinalistOrderState(prev => {
      const index = prev.indexOf(id);
      if (index <= 0) return prev;
      
      const newOrder = [...prev];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      
      try {
        localStorage.setItem(STORAGE_KEYS.FINALIST_ORDER, JSON.stringify(newOrder));
      } catch (e) {
        console.warn('[EA] Failed to save finalist order:', e);
      }
      console.log('⬆️ Moved finalist up:', id);
      return newOrder;
    });
  }, []);
  
  const moveFinalistDown = useCallback((id: string) => {
    setFinalistOrderState(prev => {
      const index = prev.indexOf(id);
      if (index === -1 || index >= prev.length - 1) return prev;
      
      const newOrder = [...prev];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      
      try {
        localStorage.setItem(STORAGE_KEYS.FINALIST_ORDER, JSON.stringify(newOrder));
      } catch (e) {
        console.warn('[EA] Failed to save finalist order:', e);
      }
      console.log('⬇️ Moved finalist down:', id);
      return newOrder;
    });
  }, []);
  
  // Demo reset: resetDemo() clears the ea_finalist_* localStorage keys and dispatches
  // 'ea_demo_reset'. Mirror the other demo hooks by clearing the LIVE state here so the
  // portfolio empties without a reload (the store itself has no cross-instance sync).
  useEffect(() => {
    const handleReset = () => {
      setFinalistIdsState(new Set());
      setFinalistOrderState([]);
      setFlaggedAssumptionsState(new Map());
      console.log('♻️ EAContext: finalists cleared by demo reset');
    };
    window.addEventListener('ea_demo_reset', handleReset);
    return () => window.removeEventListener('ea_demo_reset', handleReset);
  }, []);

  // Sync finalistOrder when finalistIds changes
  useEffect(() => {
    const idsArray = Array.from(finalistIds);
    const currentOrderSet = new Set(finalistOrder);
    
    // Add new IDs that aren't in the order
    const newIds = idsArray.filter(id => !currentOrderSet.has(id));
    
    // Remove IDs from order that are no longer finalists
    const validOrder = finalistOrder.filter(id => finalistIds.has(id));
    
    // Combine: existing valid order + new IDs
    const newOrder = [...validOrder, ...newIds];
    
    // Only update if there's a change
    if (newOrder.length !== finalistOrder.length || 
        newOrder.some((id, i) => finalistOrder[i] !== id)) {
      setFinalistOrder(newOrder);
    }
  }, [finalistIds, finalistOrder, setFinalistOrder]);
  
  // ============================================================
  // FLAGGED ASSUMPTIONS MANAGEMENT
  // ============================================================
  
  const setFlaggedAssumptions = useCallback((flags: Map<string, FlagType>) => {
    setFlaggedAssumptionsState(flags);
    try {
      localStorage.setItem(STORAGE_KEYS.FLAGGED_ASSUMPTIONS, JSON.stringify(Array.from(flags.entries())));
    } catch (e) {
      console.warn('[EA] Failed to save flagged assumptions:', e);
    }
  }, []);
  
  const setFlaggedAssumption = useCallback((oppId: string, assumptionId: string, flagType: FlagType | null) => {
    const key = `${oppId}-${assumptionId}`;
    setFlaggedAssumptionsState(prev => {
      const next = new Map(prev);
      if (flagType === null) {
        next.delete(key);
        console.log('🚩 Assumption flag removed:', key);
      } else {
        next.set(key, flagType);
        console.log('🚩 Assumption flagged:', key, flagType);
      }
      try {
        localStorage.setItem(STORAGE_KEYS.FLAGGED_ASSUMPTIONS, JSON.stringify(Array.from(next.entries())));
      } catch (e) {
        console.warn('[EA] Failed to save flagged assumptions:', e);
      }
      return next;
    });
  }, []);
  
  // ============================================================
  // LEGACY FLAG/EDIT MANAGEMENT
  // ============================================================
  
  const addFlag = useCallback((flag: Omit<EAFlag, 'id' | 'timestamp'>) => {
    const newFlag: EAFlag = {
      ...flag,
      id: `flag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };
    setFlags(prev => [...prev, newFlag]);
    console.log('🚩 EA Flag added:', newFlag);
  }, []);
  
  const removeFlag = useCallback((id: string) => {
    setFlags(prev => prev.filter(f => f.id !== id));
    console.log('🚩 EA Flag removed:', id);
  }, []);
  
  const addEdit = useCallback((edit: Omit<EAEdit, 'id' | 'timestamp'>) => {
    const newEdit: EAEdit = {
      ...edit,
      id: `edit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };
    setEdits(prev => [...prev, newEdit]);
    console.log('✏️ EA Edit added:', newEdit);
  }, []);
  
  // ============================================================
  // CONTEXT VALUE
  // ============================================================

  const value: EAContextType = {
    isEAMode,
    flags,
    addFlag,
    removeFlag,
    edits,
    addEdit,
    // Weighting
    categoryWeights,
    updateCategoryWeight,
    resetCategoryWeights,
    areCategoryWeightsCustomized,
    measureWeights,
    updateMeasureWeight,
    resetMeasureWeights,
    resetAllMeasureWeights,
    areMeasureWeightsCustomized,
    // Finalists
    finalistIds,
    setFinalistIds,
    addFinalist,
    removeFinalist,
    toggleFinalist,
    finalistOrder,
    setFinalistOrder,
    moveFinalistUp,
    moveFinalistDown,
    flaggedAssumptions,
    setFlaggedAssumptions,
    setFlaggedAssumption,
  };
  
  return (
    <EAContext.Provider value={value}>
      {children}
    </EAContext.Provider>
  );
}

export function useEA(): EAContextType {
  const context = useContext(EAContext);
  if (!context) {
    throw new Error('useEA must be used within an EAProvider');
  }
  return context;
}

export function useEAOptional(): EAContextType | null {
  return useContext(EAContext) || null;
}